/**
 * Archive document analysis — discover, analyze, and review LLM suggestions.
 */

function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
}

function formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

const CATEGORY_LABELS = {
    proposal: '<span class="badge bg-info">Proposal</span>',
    report: '<span class="badge bg-success">Report</span>',
    other: '<span class="badge bg-secondary">Other</span>',
};

/**
 * Initialize the archive analysis UI on the documents tab.
 */
export function initArchiveAnalysis(projectId) {
    const container = document.getElementById('archive-analysis');
    if (!container) return;

    const discoverBtn = document.getElementById('btn-discover-docs');
    const analyzeBtn = document.getElementById('btn-analyze-docs');

    if (discoverBtn) {
        discoverBtn.addEventListener('click', () => discoverDocuments(projectId));
    }
    if (analyzeBtn) {
        analyzeBtn.addEventListener('click', () => triggerAnalysis(projectId));
    }

    // If analysis already exists, show review button
    const status = container.dataset.analysisStatus;
    if (status === 'review' || status === 'completed') {
        showReviewSection(true);
    }

    // Auto-poll if analysis is in progress
    if (status === 'pending' || status === 'processing') {
        pollAnalysisStatus(projectId);
    }

    initReviewModal(projectId);
}


async function discoverDocuments(projectId) {
    const discoverBtn = document.getElementById('btn-discover-docs');
    const resultsDiv = document.getElementById('discover-results');
    const analyzeBtn = document.getElementById('btn-analyze-docs');

    discoverBtn.disabled = true;
    discoverBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Scanning...';
    resultsDiv.innerHTML = '';

    try {
        const resp = await fetch(`/projects/project/${projectId}/archive-analysis/discover/`);
        const data = await resp.json();

        if (data.error) {
            resultsDiv.innerHTML = `<p class="text-danger">${escapeHtml(data.error)}</p>`;
            return;
        }

        if (!data.documents || data.documents.length === 0) {
            resultsDiv.innerHTML = '<p class="text-muted">No supported documents found in archive.</p>';
            return;
        }

        // Show top 30 documents with checkboxes, sorted alphabetically by path
        const docs = data.documents.slice(0, 30);
        docs.sort((a, b) => {
            const pathA = a.folder ? `${a.folder}/${a.name}` : a.name;
            const pathB = b.folder ? `${b.folder}/${b.name}` : b.name;
            return pathA.localeCompare(pathB);
        });
        let html = `<p class="text-muted mb-2">${data.documents.length} documents found. Select documents to analyze:</p>`;
        html += '<div class="discover-docs-list">';
        html += `<label class="discover-doc-item mb-1" style="font-weight:600;">
            <input type="checkbox" id="select-all-docs"> Select all shown
        </label>`;

        for (const doc of docs) {
            const checked = '';
            const fullPath = doc.folder ? `${escapeHtml(doc.folder)}/<strong>${escapeHtml(doc.name)}</strong>` : `<strong>${escapeHtml(doc.name)}</strong>`;
            html += `<label class="discover-doc-item">
                <input type="checkbox" class="doc-checkbox" value="${escapeHtml(doc.key)}" ${checked}>
                ${CATEGORY_LABELS[doc.category] || ''}
                <span class="doc-path">${fullPath}</span>
                <span class="doc-meta">${formatSize(doc.size)}</span>
            </label>`;
        }
        html += '</div>';
        resultsDiv.innerHTML = html;

        // Select all toggle
        const selectAll = document.getElementById('select-all-docs');
        selectAll.addEventListener('change', () => {
            resultsDiv.querySelectorAll('.doc-checkbox').forEach(cb => {
                cb.checked = selectAll.checked;
            });
            updateAnalyzeButton();
        });

        resultsDiv.addEventListener('change', updateAnalyzeButton);
        analyzeBtn.style.display = '';
        updateAnalyzeButton();

    } catch (err) {
        resultsDiv.innerHTML = `<p class="text-danger">Failed to scan archive: ${escapeHtml(err.message)}</p>`;
    } finally {
        discoverBtn.disabled = false;
        discoverBtn.innerHTML = '<i class="fas fa-search"></i> Scan Archive';
    }
}


function updateAnalyzeButton() {
    const analyzeBtn = document.getElementById('btn-analyze-docs');
    const checked = document.querySelectorAll('.doc-checkbox:checked');
    if (analyzeBtn) {
        analyzeBtn.disabled = checked.length === 0;
        analyzeBtn.textContent = checked.length > 0
            ? `Analyze ${checked.length} Document${checked.length > 1 ? 's' : ''}`
            : 'Analyze Documents';
    }
}


async function triggerAnalysis(projectId) {
    const analyzeBtn = document.getElementById('btn-analyze-docs');
    const selectedKeys = Array.from(document.querySelectorAll('.doc-checkbox:checked')).map(cb => cb.value);

    if (selectedKeys.length === 0) return;

    analyzeBtn.disabled = true;
    analyzeBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Queuing...';

    const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value;

    try {
        const resp = await fetch(`/projects/project/${projectId}/archive-analysis/trigger/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            body: JSON.stringify({ documents: selectedKeys, reset: true }),
        });
        const data = await resp.json();

        if (data.success) {
            analyzeBtn.innerHTML = `<i class="fas fa-check"></i> ${data.queued} document${data.queued > 1 ? 's' : ''} queued`;
            updateStatusDisplay('processing', `Analysis in progress. ${data.queued} document${data.queued > 1 ? 's' : ''} queued for processing.`);
            showReviewSection(false);
            pollAnalysisStatus(projectId);
        } else {
            analyzeBtn.innerHTML = `<i class="fas fa-exclamation-triangle"></i> ${data.error}`;
        }
    } catch (err) {
        analyzeBtn.innerHTML = `<i class="fas fa-exclamation-triangle"></i> Failed`;
        console.error('Failed to trigger analysis:', err);
    }
}


function updateStatusDisplay(status, message) {
    const statusDiv = document.getElementById('analysis-status');
    if (!statusDiv) return;

    statusDiv.style.display = '';
    if (status === 'processing' || status === 'pending') {
        statusDiv.innerHTML = `<div class="alert alert-info"><i class="fas fa-hourglass-half"></i> ${escapeHtml(message)}</div>`;
    } else if (status === 'review' || status === 'completed') {
        statusDiv.innerHTML = `<div class="alert alert-success"><i class="fas fa-check-circle"></i> ${escapeHtml(message)}</div>`;
    } else if (status === 'failed') {
        statusDiv.innerHTML = `<div class="alert alert-danger"><i class="fas fa-exclamation-circle"></i> ${escapeHtml(message)}</div>`;
    }
}


async function pollAnalysisStatus(projectId, attempts = 0) {
    if (attempts > 60) return; // Stop after 5 minutes

    await new Promise(resolve => setTimeout(resolve, 5000));

    try {
        const resp = await fetch(`/projects/project/${projectId}/archive-analysis/suggestions/`);
        const data = await resp.json();

        if (data.analysis_status === 'review' || data.analysis_status === 'completed') {
            const count = data.suggestions?.length || 0;
            updateStatusDisplay('review', `Analysis complete! ${count} suggestion${count !== 1 ? 's' : ''} available.`);
            showReviewSection(true);
            return;
        }

        if (data.analysis_status === 'failed') {
            updateStatusDisplay('failed', 'Analysis failed.');
            return;
        }

        // Still processing, keep polling
        pollAnalysisStatus(projectId, attempts + 1);
    } catch {
        pollAnalysisStatus(projectId, attempts + 1);
    }
}


function showReviewSection(show) {
    const reviewSection = document.getElementById('review-section');
    if (reviewSection) {
        reviewSection.style.display = show ? '' : 'none';
    }
}


function initReviewModal(projectId) {
    const modal = document.getElementById('analysisModal');
    if (!modal) return;

    // Move modal to body to avoid stacking context issues
    document.body.appendChild(modal);

    // Load suggestions when modal opens (Bootstrap fires this event)
    modal.addEventListener('shown.bs.modal', () => {
        loadSuggestions(projectId);
    });

    const selectAll = document.getElementById('selectAllAnalysis');
    if (selectAll) {
        selectAll.addEventListener('change', () => {
            modal.querySelectorAll('.analysis-checkbox').forEach(cb => {
                cb.checked = selectAll.checked;
            });
            updateApplyButton();
        });
    }

    const body = document.getElementById('analysisBody');
    if (body) {
        body.addEventListener('change', updateApplyButton);
    }

    const applyBtn = document.getElementById('applyAnalysisBtn');
    if (applyBtn) {
        applyBtn.addEventListener('click', () => applySuggestions(projectId));
    }

    const dismissBtn = document.getElementById('dismissAnalysisBtn');
    if (dismissBtn) {
        dismissBtn.addEventListener('click', () => applySuggestions(projectId, true));
    }
}


function updateApplyButton() {
    const checked = document.querySelectorAll('.analysis-checkbox:checked');
    const applyBtn = document.getElementById('applyAnalysisBtn');
    if (applyBtn) {
        applyBtn.disabled = checked.length === 0;
        applyBtn.textContent = checked.length > 0
            ? `Apply Selected (${checked.length})`
            : 'Apply Selected';
    }
}


async function loadSuggestions(projectId) {
    const loading = document.getElementById('analysisLoading');
    const empty = document.getElementById('analysisEmpty');
    const table = document.getElementById('analysisTable');
    const body = document.getElementById('analysisBody');
    const docsInfo = document.getElementById('docsAnalyzedInfo');

    loading.style.display = '';
    empty.style.display = 'none';
    table.style.display = 'none';
    body.innerHTML = '';
    if (docsInfo) docsInfo.innerHTML = '';

    try {
        const resp = await fetch(`/projects/project/${projectId}/archive-analysis/suggestions/`);
        const data = await resp.json();

        loading.style.display = 'none';

        // Show analyzed documents info
        if (docsInfo && data.documents_analyzed) {
            const docNames = data.documents_analyzed.map(d => escapeHtml(d.name)).join(', ');
            docsInfo.innerHTML = `<small class="text-muted">Analyzed: ${docNames}</small>`;
        }

        if (!data.suggestions || data.suggestions.length === 0) {
            empty.style.display = '';
            return;
        }

        table.style.display = '';
        for (const s of data.suggestions) {
            const row = document.createElement('tr');
            const isHtml = s.field === 'description' || s.field === 'methodology';
            const currentDisplay = isHtml ? s.current : escapeHtml(s.current);
            const suggestedDisplay = isHtml ? s.suggested : escapeHtml(s.suggested);

            row.innerHTML = `
                <td><input type="checkbox" class="analysis-checkbox" value="${escapeHtml(s.field)}" checked></td>
                <td><strong>${escapeHtml(s.label)}</strong></td>
                <td class="suggestion-current">${currentDisplay}</td>
                <td class="suggestion-proposed text-primary">${suggestedDisplay}</td>
            `;
            body.appendChild(row);
        }
        updateApplyButton();
    } catch (err) {
        loading.style.display = 'none';
        body.innerHTML = `<tr><td colspan="4" class="text-danger">Failed to load suggestions.</td></tr>`;
        table.style.display = '';
    }
}


async function applySuggestions(projectId, dismissAll = false) {
    const fields = dismissAll
        ? []
        : Array.from(document.querySelectorAll('.analysis-checkbox:checked')).map(cb => cb.value);

    const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value;

    try {
        const resp = await fetch(`/projects/project/${projectId}/archive-analysis/apply/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            body: JSON.stringify({ fields }),
        });
        const data = await resp.json();

        if (data.success) {
            window.location.reload();
        }
    } catch (err) {
        console.error('Failed to apply suggestions:', err);
    }
}
